// components/GiftModal.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { useCanvaAuth } from '../contexts/CanvaAuthContext2.tsx';

export type GiftSurveyOption = { value: string; key: string };

type GiftModalProps = {
  show: boolean;
  onClose: () => void;
};

const GiftModal: React.FC<GiftModalProps> = ({ show, onClose }) => {
  const [modalStep, setModalStep] = useState(0);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [selectedSurveyValue, setSelectedSurveyValue] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fade, setFade] = useState(true);
  const { canvaAuthenticated, token } = useCanvaAuth();

  const env = canvaAuthenticated?.env;

  const giftSurveyOptions = useMemo((): GiftSurveyOption[] => {
    const raw = canvaAuthenticated?.userProfile?.giftSurveyOptions;
    if (!Array.isArray(raw)) return [];
    return raw.filter(
      (o: unknown): o is GiftSurveyOption =>
        !!o && typeof o === 'object' && 'value' in o && 'key' in o
    );
  }, [canvaAuthenticated?.userProfile?.giftSurveyOptions]);

  const hasSurvey = giftSurveyOptions.length > 0;
  const emailStepIndex = hasSurvey ? 1 : 0;
  const thanksStepIndex = hasSurvey ? 2 : 1;

  useEffect(() => {
    if (!show) return;
    setModalStep(0);
    setEmail('');
    setEmailError('');
    setSelectedSurveyValue(null);
    setFade(true);
  }, [show]);

  const handleFadeChange = (nextStep: number) => {
    setFade(false);
    setTimeout(() => {
      setModalStep(nextStep);
      setFade(true);
    }, 200);
  };

  const validateEmail = (email: string): { isValid: boolean; error?: string } => {
    const trimmedEmail = email.trim();
    
    // Empty check
    if (!trimmedEmail) {
      return { isValid: false, error: 'Please enter your email address' };
    }

    // Length checks (RFC 5321: local part max 64, domain max 255, total max 254)
    if (trimmedEmail.length > 254) {
      return { isValid: false, error: 'Email address is too long (maximum 254 characters)' };
    }

    // Basic structure check - must contain @
    if (!trimmedEmail.includes('@')) {
      return { isValid: false, error: 'Email must contain @ symbol' };
    }

    const parts = trimmedEmail.split('@');
    
    // Must have exactly one @
    if (parts.length !== 2) {
      return { isValid: false, error: 'Email must contain exactly one @ symbol' };
    }

    const [localPart, domain] = parts;

    // Local part checks
    if (!localPart || localPart.length === 0) {
      return { isValid: false, error: 'Email must have a local part before @' };
    }

    if (localPart.length > 64) {
      return { isValid: false, error: 'Local part of email is too long (maximum 64 characters)' };
    }

    // No leading or trailing dots in local part
    if (localPart.startsWith('.') || localPart.endsWith('.')) {
      return { isValid: false, error: 'Email cannot start or end with a dot' };
    }

    // No consecutive dots
    if (localPart.includes('..')) {
      return { isValid: false, error: 'Email cannot contain consecutive dots' };
    }

    // Domain checks
    if (!domain || domain.length === 0) {
      return { isValid: false, error: 'Email must have a domain after @' };
    }

    if (domain.length > 255) {
      return { isValid: false, error: 'Domain part of email is too long (maximum 255 characters)' };
    }

    // Domain must contain at least one dot
    if (!domain.includes('.')) {
      return { isValid: false, error: 'Domain must contain at least one dot (e.g., example.com)' };
    }

    // No leading or trailing dots in domain
    if (domain.startsWith('.') || domain.endsWith('.')) {
      return { isValid: false, error: 'Domain cannot start or end with a dot' };
    }

    // No consecutive dots in domain
    if (domain.includes('..')) {
      return { isValid: false, error: 'Domain cannot contain consecutive dots' };
    }

    // Check for valid TLD (at least 2 characters after last dot)
    const domainParts = domain.split('.');
    const tld = domainParts[domainParts.length - 1];
    if (!tld || tld.length < 2) {
      return { isValid: false, error: 'Domain must have a valid top-level domain (e.g., .com, .org)' };
    }

    // TLD should only contain letters
    if (!/^[a-zA-Z]+$/.test(tld)) {
      return { isValid: false, error: 'Top-level domain must contain only letters' };
    }

    // Character validation - RFC 5322 compliant pattern
    // Local part: allows letters, numbers, and these special chars: . ! # $ % & ' * + - / = ? ^ _ ` { | } ~
    // Domain: allows letters, numbers, dots, and hyphens
    const localPartRegex = /^[a-zA-Z0-9.!#$%&'*+\-/=?^_`{|}~]+$/;
    const domainRegex = /^[a-zA-Z0-9.-]+$/;

    if (!localPartRegex.test(localPart)) {
      return { isValid: false, error: 'Local part contains invalid characters' };
    }

    if (!domainRegex.test(domain)) {
      return { isValid: false, error: 'Domain contains invalid characters' };
    }

    // Check for common typos in TLD (only invalid ones)
    const commonTypos: { [key: string]: string } = {
      'con': 'com',
      'cpm': 'com',
      'cm': 'com',
      'om': 'com',
      'coom': 'com',
      'ogr': 'org',
      'nrt': 'net',
      'ner': 'net',
    };

    const lowerTld = tld.toLowerCase();
    if (commonTypos[lowerTld]) {
      return { isValid: false, error: `Did you mean .${commonTypos[lowerTld]}?` };
    }

    // Check for spaces anywhere
    if (trimmedEmail.includes(' ')) {
      return { isValid: false, error: 'Email cannot contain spaces' };
    }

    // Final comprehensive regex check (more permissive but catches edge cases)
    const comprehensiveEmailRegex = /^[a-zA-Z0-9.!#$%&'*+\-/=?^_`{|}~]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    
    if (!comprehensiveEmailRegex.test(trimmedEmail)) {
      return { isValid: false, error: 'Please enter a valid email address' };
    }

    // Check for obviously fake patterns
    const fakePatterns = [
      /^test@/i,
      /^admin@/i,
      /^noreply@/i,
      /^no-reply@/i,
      /@example\./i,
      /@test\./i,
      /@localhost/i,
      /@123\./i,
    ];

    // Allow these patterns but warn (or you can block them)
    // For now, we'll allow them but you can uncomment to block:
    // for (const pattern of fakePatterns) {
    //   if (pattern.test(trimmedEmail)) {
    //     return { isValid: false, error: 'Please use a valid personal or business email' };
    //   }
    // }

    return { isValid: true };
  };

  const postClaimGiftRequest = async (payload: {
    giftId: string;
    email?: string;
    surveyValue?: string;
  }) => {
    return fetch(env!.claimGiftUrl!, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
  };

  const handleSubmitEmailAndClaimGift = async () => {
    setEmailError('');

    const validation = validateEmail(email);
    if (!validation.isValid) {
      setEmailError(validation.error || 'Please enter a valid email address');
      return;
    }

    if (!token || !env?.claimGiftUrl) {
      setEmailError('Unable to process request. Please try again.');
      return;
    }

    const giftId = canvaAuthenticated?.userProfile?.gift;
    if (!giftId) {
      setEmailError('Unable to process request. Please try again.');
      return;
    }

    setIsSubmitting(true);

    try {
      const body: { giftId: string; email: string; surveyValue?: string } = {
        giftId,
        email: email.trim(),
      };
      if (hasSurvey && selectedSurveyValue) {
        body.surveyValue = selectedSurveyValue;
      }

      const res = await postClaimGiftRequest(body);

      if (!res.ok) {
        setEmailError('Failed to claim gift. Please try again.');
        return;
      }

      handleFadeChange(thanksStepIndex);
    } catch (error) {
      console.error('Failed to claim gift:', error);
      setEmailError('Failed to claim gift. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  /** Survey answered on step 1 but user dismisses email step: still claim gift + persist survey. */
  const handleDismiss = async () => {
    if (modalStep === thanksStepIndex) {
      onClose();
      return;
    }

    if (isSubmitting) {
      onClose();
      return;
    }

    if (
      modalStep === emailStepIndex &&
      hasSurvey &&
      selectedSurveyValue &&
      token &&
      env?.claimGiftUrl &&
      canvaAuthenticated?.userProfile?.gift
    ) {
      setIsSubmitting(true);
      try {
        const res = await postClaimGiftRequest({
          giftId: String(canvaAuthenticated.userProfile.gift),
          surveyValue: selectedSurveyValue,
        });
        if (!res.ok) {
          console.warn('Gift modal partial claim failed:', await res.text());
        }
      } catch (e) {
        console.error('Gift modal partial claim:', e);
      } finally {
        setIsSubmitting(false);
        onClose();
      }
      return;
    }

    onClose();
  };

  const modalTitle =
    modalStep === thanksStepIndex
      ? env?.strings['gift-modal-page-3-title'] || '🙏 Thank You'
      : modalStep === emailStepIndex
        ? env?.strings['gift-modal-page-1-title'] || '🎉 A Little Gift for You'
        : env?.strings['gift-modal-survey-title'] || 'Quick question';

  return (
    <Modal
      show={show}
      onHide={handleDismiss}
      contentClassName="gift-modal canva-gift-modal"
      centered
      backdrop={true}
    >
      <div className={`transition-opacity ${fade ? 'opacity-100' : 'opacity-0'} transition duration-300`}>
        <Modal.Header>
          <Modal.Title>{modalTitle}</Modal.Title>
          <Button variant="close" aria-label="Close" onClick={() => void handleDismiss()} />
        </Modal.Header>

        <Modal.Body>
          {modalStep === 0 && hasSurvey && (
            <>
              <div className="px-2 mb-3" id="gift-modal-survey-heading">
                <h5 className="mb-2 text-center">
                  {env?.strings['gift-modal-survey-line-1'] ||
                    'What are you mainly working on in Canva?'}
                </h5>
                {env?.strings['gift-modal-survey-line-2'] ? (
                  <p className="mb-0 text-center text-muted small">
                    {env.strings['gift-modal-survey-line-2']}
                  </p>
                ) : null}
              </div>
              <div
                className="gift-modal-survey-options d-flex flex-column gap-2 mb-3"
                role="radiogroup"
                aria-labelledby="gift-modal-survey-heading"
              >
                {giftSurveyOptions.map((opt) => {
                  const label = env?.strings[opt.key] ?? opt.value;
                  const selected = selectedSurveyValue === opt.value;
                  return (
                    <Button
                      key={opt.value}
                      type="button"
                      variant={selected ? 'primary' : 'outline-primary'}
                      className="w-100 text-start py-2"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => {
                        setSelectedSurveyValue(opt.value);
                        handleFadeChange(emailStepIndex);
                      }}
                    >
                      {label}
                    </Button>
                  );
                })}
              </div>
            </>
          )}

          {modalStep === emailStepIndex && (
            <>
              <div className="text-center px-2 mb-3">
                <h5 className="mb-3">{env?.strings['gift-modal-page-1-line-1']}</h5>
                <p className="mb-2">{env?.strings['gift-modal-page-1-line-2']}</p>
                <p className="mb-3">
                  <strong>{env?.strings['gift-modal-page-1-line-3']}</strong>
                </p>
              </div>
              <Form>
                <Form.Group className="mb-3">
                  <Form.Control
                    type="email"
                    placeholder={
                      env?.strings['gift-modal-email-placeholder'] || 'your.email@example.com'
                    }
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setEmailError('');
                    }}
                    isInvalid={!!emailError}
                  />
                  {emailError && (
                    <Form.Control.Feedback type="invalid">{emailError}</Form.Control.Feedback>
                  )}
                </Form.Group>
                <div className="d-flex justify-content-center">
                  <Button
                    variant="primary"
                    onClick={handleSubmitEmailAndClaimGift}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        />
                        {env?.strings['gift-modal-claiming-text'] || 'Claiming...'}
                      </>
                    ) : (
                      <>{env?.strings['gift-modal-page-1-btn-claim'] || '🎁 Claim Your Gift'}</>
                    )}
                  </Button>
                </div>
              </Form>
            </>
          )}

          {modalStep === thanksStepIndex && (
            <div className="text-center px-2">
              <p>
                {env?.strings['gift-modal-page-3-line-1'] ||
                  'Thanks a lot! We really appreciate you using AI Music.'}
              </p>
              <Button variant="primary" onClick={() => void handleDismiss()}>
                {env?.strings['gift-modal-close-btn'] || 'Close'}
              </Button>
            </div>
          )}
        </Modal.Body>
      </div>
    </Modal>
  );
};

export default GiftModal;
